/**
 * WallSketch Editor Tool
 *
 * Tool for generating and editing a skeleton graph from room polygons.
 * The skeleton represents wall intersections as nodes connected by edges.
 *
 * Part of the refinement mode in the floorplan persona.
 */

import { FloorplanToolBase } from './FloorplanToolBase';
import { Viewer } from '../../../viewer/viewer';
import { IPersona, IViewConfig } from '../../types';
import { ViewModes } from '../../helpers';
import { DetectedFloorPlane } from '../../../utils/floorDetection';
import { PointoramaRoom } from '../../../utils/PointoramaRoom';
import { PointoramaWallSketch } from '../../../utils/PointoramaWallSketch';
import * as THREE from 'three';
import { generateSkeletonFromRooms, FloorExpansionConfig, SkeletonDebugData } from './FloorProcessors';
import { SkeletonProcessingPipeline, FloorSkeleton, SkeletonNode, SkeletonEdge } from '../../../utils/SkeletonProcessingPipeline';
import { appendExistingOutlineSubgraph, getSharedSkeletonPipeline, maybeConvertLegacyOutline, ringsToWallRegion } from '../../../utils/skeleton/buildLayoutWallSketch';
import { applyWeldedEdgeSizes } from '../../../utils/skeleton/sdfPartition';
import { extractOutlineRings, type OutlineRingComponent } from '../../../utils/skeleton/outlineRings';
import { FloorHeightInfo } from '../../../utils/skeleton/resolveSkeletonHeight';
import { applyFixtureJsonToWallSketch, downloadSkeletonFixture } from '../../../utils/skeleton/skeletonFixture';
import { FloorplanPersona } from '../../personas/implementations/FloorplanPersona';
import { PointoramaOutline } from '../../../utils/PointoramaOutline';
import { WallSketchEditSession, WallSketchSessionCapabilities, DEFAULT_SESSION_CAPABILITIES } from './WallSketchEditSession';
import { hasGlobalPropertyService, isZWithinLevel, FALLBACK_INTERIOR_HEIGHT_M } from '@pointorama/potree-shared';
import { getShowDebugUiProperty } from '../../../../modules/Properties/integration/UserSettingsIntegration';
import { DragEventData } from '../../BaseTool';
import {
  SkeletonDebugWidget,
  SKELETON_RAYCAST_DEBUG_GROUP_NAME,
  SKELETON_BISECTOR_DEBUG_GROUP_NAME,
  SKELETON_STRAIGHTEN_DEBUG_GROUP_NAME,
  SKELETON_SOURCE_VERTEX_DEBUG_GROUP_NAME,
  SKELETON_PROBE_ORIGIN_DEBUG_GROUP_NAME,
  SKELETON_PROBE_RAY_DEBUG_GROUP_NAME,
  SKELETON_PROBE_MIDPOINT_DEBUG_GROUP_NAME,
  SKELETON_PROBE_CENTERLINE_DEBUG_GROUP_NAME,
  SKELETON_PAIR_WALLS_MATCH_NEAR_DEBUG_GROUP_NAME,
  SKELETON_PAIR_WALLS_RAY_NEAR_DEBUG_GROUP_NAME,
  SKELETON_PAIR_WALLS_MATCH_INF_DEBUG_GROUP_NAME,
  SKELETON_PAIR_WALLS_RAY_INF_DEBUG_GROUP_NAME,
  SKELETON_PRUNE_MATCH_DEBUG_GROUP_NAME,
  SKELETON_CORNER_MATCH_DEBUG_GROUP_NAME,
  SKELETON_SPLIT_LINES_DEBUG_GROUP_NAME,
  SKELETON_RASTER_DEBUG_GROUP_NAME,
} from '../widgets/SkeletonDebugWidget';
import { SDFDebugWidget } from '../widgets/SDFDebugWidget';
import { createDebugGroup, addRayToDebugGroup, addPointToDebugGroup } from '../../../utils/DebugVisualization';
// Re-export types for backwards compatibility
export type { FloorSkeleton, SkeletonNode, SkeletonEdge };

export class WallSketchEditorTool extends FloorplanToolBase {
  readonly name: string = 'wall_sketch_editor';

  // WallSketch data (for processing pipeline - intermediate state before saving)
  private generatedWallSketch: FloorSkeleton | null = null;
  private debugData: SkeletonDebugData | null = null;

  // Processing pipeline
  private pipeline: SkeletonProcessingPipeline;

  // Debug widgets
  private debugWidget: SkeletonDebugWidget | null = null;
  private sdfDebugWidget: SDFDebugWidget | null = null;

  // Hover tooltip — a thin renderer over `skeleton.getHover()`. The
  // session's `updateHover` writes the hit result into that shared
  // state; this tool only reads it and renders details. Marker
  // highlight is also owned by `WallSketchHover`, so no local
  // `hoveredNodeId` to keep in sync.
  private hoverTooltip: HTMLDivElement | null = null;

  // Interactive editing (click-to-enter, right-click delete, ESC/Enter)
  private editSession: WallSketchEditSession;

  // Floor expansion configuration
  private readonly expansionConfig: Partial<FloorExpansionConfig> = {
    innerWallThickness: 0.15,
    outerWallThickness: 0.3,
    maxRayDistance: 0.5,
    mergeTolerance: 0.05,
  };

  /** Gesture capabilities forwarded to the edit session. The base editor runs
   * with everything on; the wall EDIT tool subclass strips creation. */
  protected readonly capabilities: WallSketchSessionCapabilities;

  constructor(viewer: Viewer, persona: IPersona, capabilities: WallSketchSessionCapabilities = DEFAULT_SESSION_CAPABILITIES) {
    super(viewer, persona);
    this.capabilities = capabilities;
    this.initUndoRedo();
    // The SHARED pipeline instance (also used by the WallCreateTool preview /
    // commit and the layout bootstrap) — pass edits in this tool's debug
    // window affect every generation path, and vice versa.
    this.pipeline = getSharedSkeletonPipeline();

    this.editSession = new WallSketchEditSession({
      viewer: this.viewer,
      getSceneWallSketch: () => this.getCurrentFloorWallSketch(),
      getUndoRedo: () => this.undoRedo,
      onWallSketchRestored: () => {
        // Pipeline-derived state is now stale; next regenerate will rebuild it.
        this.generatedWallSketch = null;
        this.updateDebugVisualization();
      },
      dispatchPhase: (phase) => this.dispatchStateChanged({ phase }),
      buildFloorSkeleton: () => this.buildFloorSkeletonFromCurrentState(),
      capabilities,
    });
  }

  /**
   * Build a pipeline-style FloorSkeleton from the **current scene skeleton**
   * (with all interactive edits applied) plus the current floor's rooms.
   * Used by the session for on-demand operations like wall-width recompute,
   * which need the room polygons + the up-to-date node/edge topology.
   *
   * Approach: run `generateBaseSkeleton` to get a fresh FloorSkeleton with
   * `roomConnectivity` populated, then OVERWRITE its nodes and edges with
   * the scene skeleton's current state. The room data the helpers need
   * (raycast against rooms, etc.) comes from the fresh build; the topology
   * matches what the user sees on screen.
   */
  private buildFloorSkeletonFromCurrentState(): FloorSkeleton | null {
    const sceneWallSketch = this.getCurrentFloorWallSketch();
    if (!sceneWallSketch) return null;
    const baseResult = this.generateBaseSkeleton();
    if (!baseResult) return null;
    const fs = baseResult.skeleton;

    // Replace the freshly-built nodes/edges with the scene's current state.
    // FloorSkeleton uses Map<string, SkeletonNode/Edge> with extra fields
    // (sourceVertices, probedHits, selected, etc.) that aren't on the
    // persisted shape — fill them with empty defaults; the wall-thickness
    // pass only reads position / startNodeId / endNodeId / leftSize /
    // rightSize / isJunctionEdge.
    fs.nodes = new Map(
      sceneWallSketch.getNodes().map((n) => [
        n.id,
        {
          id: n.id,
          position: new THREE.Vector3(n.position.x, n.position.y, n.position.z),
          roomIds: [...n.roomIds],
          edgeIds: [...n.edgeIds],
          sourceVertices: [],
          probedHits: [],
          selected: false,
          metadata: n.metadata ?? undefined,
        },
      ]),
    );
    fs.edges = new Map(
      sceneWallSketch.getAllEdges().map((e) => [
        e.id,
        {
          id: e.id,
          startNodeId: e.startNodeId,
          endNodeId: e.endNodeId,
          roomIds: [...e.roomIds],
          leftSize: e.leftSize,
          rightSize: e.rightSize,
          wallType: e.wallType,
          selected: false,
          metadata: e.metadata ?? undefined,
          isJunctionEdge: false,
        },
      ]),
    );
    fs.floorHeight = sceneWallSketch.getFloorHeight();
    return fs;
  }

  /**
   * Scale every wall sketch's node markers (1.2 = oversized grab handles while
   * this editor is active, 0.6 = shrunken elsewhere) and remember the value on
   * the scene so marker rebuilds outside the tool re-apply the same size.
   */
  private setWallSketchSphereScale(scale: number): void {
    this.viewer.scene.wallSketchSphereScale = scale;
    for (const sketch of this.viewer.scene.wallSketches) {
      for (const sphere of sketch.spheres) sphere.setScaleFactor(scale);
    }
  }

  protected onEnable(): void {
    super.onEnable();
    this.viewer.renderer.domElement.style.cursor = 'crosshair';
    this.setWallSketchSphereScale(1.2);

    // Show the base hint immediately ("click a node or wall to edit the skeleton").
    // Edit-mode entry will switch this to the keyboard cheat sheet.
    this.dispatchStateChanged({ phase: 'idle' });

    // Create hover tooltip
    this.createHoverTooltip();

    // Add mouse move listener for hover detection
    this.viewer.renderer.domElement.addEventListener('mousemove', this.handleMouseMoveForHover);

    // Listen for skeleton added events to generate skeleton when annotation is created
    this.viewer.scene.addEventListener('wall_sketch_added', this.handleWallSketchAdded);

    // Create and register debug widgets
    if (this.viewer.debugOverlay) {
      if (!this.debugWidget) {
        this.debugWidget = new SkeletonDebugWidget(this.viewer, {
          onRegenerateWallSketch: () => this.generateWallSketch(),
          onRunPass: (passName) => this.runPass(passName),
          onExportFixture: () => this.exportSkeletonFixture(),
          onLoadFixture: (json) => this.loadWallSketchFixture(json),
          onDebugOptionsChange: (opts) => {
            this.updateSplitLineOverlay(opts.showSplitLines);
            this.updateShowAllTriangles(opts.showAllTriangles);
          },
        });
        this.debugWidget.setPipeline(this.pipeline);
      }
      this.viewer.debugOverlay.addWidget(this.debugWidget);

      // Create and register SDF debug widget
      if (!this.sdfDebugWidget) {
        this.sdfDebugWidget = new SDFDebugWidget();
      }
      this.viewer.debugOverlay.addWidget(this.sdfDebugWidget);

      // Update SDF widget with current skeleton's SDF data if available
      this.updateSDFDebugWidget();
    }

    // Measured floors: fold the legacy OUTLINE annotation into the sketch on
    // first edit, so the wall handles operate on the merged outline rings.
    this.maybeConvertLegacyOutlineForCurrentFloor();

    // The tool is now a pure editor: it does NOT create or auto-generate a
    // skeleton on enable. WallSketch creation is driven entirely by the Layout
    // bootstrap dialog (LayoutPersona) and the floor right-click "generate wall
    // sketch" action. If a skeleton already exists we still bootstrap its
    // legacy wall-region overlay so walls render without a manual recompute.
    if (this.currentFloorGroupIdentifier && this.currentFloorRoomIdentifiers.length > 0) {
      const existingWallSketch = this.getCurrentFloorWallSketch();
      if (existingWallSketch && existingWallSketch.getNodes().length > 0 && existingWallSketch.getWallRegions().length === 0) {
        // Legacy skeleton missing the wall-region overlay — bootstrap it once
        // now. Future skeleton generations populate this automatically via
        // updateSceneWallSketch().
        this.recomputeWallRegionsForCurrentFloor(existingWallSketch);
      }
    }

    // Feed floor heights so per-element height constraints resolve in 3D.
    this.pushFloorGroupsToCurrentWallSketch();
  }

  protected onDisable(): void {
    this.clearSketchCreationListener();
    this.pendingDrawDragStart = null;
    // Auto-confirm any in-progress edit so the user's work persists when
    // they leave the tool. Previously this called `cancel()` and silently
    // discarded changes — surprising when switching tools mid-edit. ESC
    // inside the session is still the explicit "discard" path.
    if (this.editSession.isEditing) {
      this.editSession.commit();
    }

    this.setWallSketchSphereScale(0.6);
    this.viewer.renderer.domElement.style.cursor = '';

    // Remove mouse move listener
    this.viewer.renderer.domElement.removeEventListener('mousemove', this.handleMouseMoveForHover);

    // Remove skeleton added listener
    this.viewer.scene.removeEventListener('wall_sketch_added', this.handleWallSketchAdded);

    // Clean up hover tooltip
    this.destroyHoverTooltip();

    // Clean up debug visualization
    this.clearDebugVisualization();

    // Remove debug widgets
    if (this.viewer.debugOverlay) {
      if (this.debugWidget) {
        try {
          this.viewer.debugOverlay.removeWidget(this.debugWidget.name);
        } catch {
          // Widget may already be removed
        }
      }
      if (this.sdfDebugWidget) {
        try {
          this.viewer.debugOverlay.removeWidget(this.sdfDebugWidget.name);
        } catch {
          // Widget may already be removed
        }
      }
    }

    this.generatedWallSketch = null;
    this.debugData = null;

    super.onDisable();
  }

  getViewConfig(): IViewConfig {
    return {
      views: [ViewModes.XRAY_2D, ViewModes.PLAN_2D_SKELETON, ViewModes.PERSPECTIVE_3D_RGB],
      defaultViewId: ViewModes.XRAY_2D.id,
    };
  }

  /**
   * Called when floor selection changes.
   * Does NOT create or regenerate the skeleton — the tool is a pure editor.
   * WallSketch creation is driven by the Layout bootstrap dialog and the floor
   * right-click "generate wall sketch" action. Here we only reset local
   * pipeline-derived state for the new floor.
   */
  protected onFloorChanged(_floor: DetectedFloorPlane | null, _ceiling: DetectedFloorPlane | null, _groupIdentifier: string | null): void {
    // Clear local generated skeleton state when switching floors.
    this.generatedWallSketch = null;
    this.debugData = null;
    this.clearDebugVisualization();

    // Feed floor heights to the newly-active floor's skeleton.
    this.pushFloorGroupsToCurrentWallSketch();

    // Measured floors convert their legacy outline on first edit.
    this.maybeConvertLegacyOutlineForCurrentFloor();

    // Update SDF debug widget for new floor's skeleton
    this.updateSDFDebugWidget();
  }

  /**
   * Legacy-outline conversion for the active floor (Measured representation
   * only — see maybeConvertLegacyOutline). Snapshots the existing sketch
   * first so the conversion is one undoable step; undoing it re-shows the
   * legacy outline (tombstone hiding is derived from the merged edges).
   */
  private maybeConvertLegacyOutlineForCurrentFloor(): void {
    const floorId = this.currentFloorGroupIdentifier;
    if (!floorId) return;
    const floorHeight = this.currentFloorFloorPlane?.height ?? 0;
    maybeConvertLegacyOutline(this.viewer, floorId, floorHeight, {
      // One undoable step: undoing the conversion drops the merged edges,
      // which automatically re-shows the legacy outline (tombstone hiding is
      // derived from the merged-edge presence). Only possible when a sketch
      // already exists — a request_skeleton-created one starts empty anyway.
      onBeforeConvert: () => {
        if (this.getCurrentFloorWallSketch()) this.editSession.pushWallSketchUndoSnapshot();
      },
    });
  }

  /**
   * Get the skeleton for the current floor from the scene
   */
  private getCurrentFloorWallSketch(): PointoramaWallSketch | null {
    if (!this.currentFloorGroupIdentifier) return null;

    return this.viewer.scene.wallSketches.find((skeleton) => skeleton.groupIdentifier === this.currentFloorGroupIdentifier) || null;
  }

  /**
   * Push the project's floor groups (heights) into the current floor's skeleton
   * so per-node / per-edge height constraints can resolve their default
   * (next floor / ceiling) and explicit target-floor heights during 3D
   * extrusion.
   */
  private pushFloorGroupsToCurrentWallSketch(): void {
    const skeleton = this.getCurrentFloorWallSketch();
    if (!skeleton) return;
    const floors: FloorHeightInfo[] = (this.persona as FloorplanPersona).getFloorGroupsData().map((g) => ({
      groupIdentifier: g.groupIdentifier,
      floorHeight: g.floor.height,
      ceilingHeight: g.ceiling?.height,
    }));
    skeleton.setFloorGroupsData(floors);
  }

  /**
   * Capture EVERYTHING about the current floor's skeleton into one self-contained
   * fixture and download it as JSON. Wired to the debug menu's "Export WallSketch
   * (full)" button. Supersedes the old separate "Test Fixture" and "Center Edges
   * Fixture" exports — it includes their union:
   *   - nodes + edges (with thickness + roomIds), wallRegions, floorGroups,
   *     metadata, and the extruded geometry summary + raw positions (skinning);
   *   - roomConnectivity (room + `__OUTER__` outline polygons) so the room-aware
   *     passes can be re-run from real geometry;
   *   - pipelineConfig (the skeleton stack: pass list, kwargs, enabled flags) so
   *     a bug report reproduces the exact processing that produced this skeleton.
   */
  private exportSkeletonFixture(): void {
    const skeleton = this.getCurrentFloorWallSketch();
    if (!skeleton) {
      console.warn('Export skeleton fixture: no skeleton on the current floor');
      return;
    }
    // roomConnectivity lives on the FloorSkeleton built from the current scene
    // state, not on the PointoramaWallSketch — gather it here.
    const fs = this.buildFloorSkeletonFromCurrentState();
    const roomConnectivity = fs?.roomConnectivity?.map((r) => ({
      identifier: r.identifier,
      polygon: r.polygon.map((p) => ({ x: p.x, y: p.y })),
    }));
    const fixture = skeleton.captureFixture(`floor ${this.currentFloorGroupIdentifier ?? 'unknown'}`, {
      roomConnectivity,
      pipelineConfig: this.pipeline.getConfig(),
    });
    downloadSkeletonFixture(fixture, `skeleton-fixture-${skeleton.getNodes().length}n-${skeleton.getAllEdges().length}e.json`);
  }

  /**
   * Load a previously-exported skeleton fixture JSON onto the current floor's
   * skeleton and re-render it (incl. the skinned walls). Wired to the debug
   * menu's "Load Test Fixture" button — lets us pull a captured/test fixture
   * (e.g. the ones under `skeleton/__tests__/fixtures`) into the live app to
   * inspect the partition/skinning visually. Replaces the current floor's
   * skeleton geometry in memory only; it does NOT persist the edit.
   */
  private loadWallSketchFixture(json: string): void {
    const skeleton = this.getCurrentFloorWallSketch();
    if (!skeleton) {
      console.warn('Load skeleton fixture: no skeleton on the current floor');
      return;
    }
    applyFixtureJsonToWallSketch(skeleton, json);
  }

  /**
   * Rebuild the "wall split lines" debug overlay: the per-element partition
   * boundaries (edge quads + node n-gons) drawn as 2D line segments at floor Z.
   * Recreated each call so it tracks skeleton edits; visibility is preserved by
   * createDebugGroup, and the widget checkbox toggles it.
   */
  private updateSplitLineOverlay(show: boolean): void {
    const group = createDebugGroup(this.viewer, SKELETON_SPLIT_LINES_DEBUG_GROUP_NAME, { visible: show });
    if (!show) return;
    const skeleton = this.getCurrentFloorWallSketch();
    if (!skeleton) return;
    const segments = skeleton.getPartitionSegments();
    if (segments.length === 0) return;
    const positions: number[] = [];
    for (const s of segments) positions.push(s.a.x, s.a.y, s.a.z, s.b.x, s.b.y, s.b.z);
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    const lines = new THREE.LineSegments(geometry, new THREE.LineBasicMaterial({ color: 0xff00ff, depthTest: false }));
    lines.renderOrder = 999;
    group.add(lines);
  }

  /**
   * Toggle the "show all triangles" debug mode. When on, the wall outline
   * overlay shows every triangle edge in the extruded mesh (no crease
   * filter). Re-renders the current floor's skinning so the change takes
   * effect immediately.
   */
  private updateShowAllTriangles(show: boolean): void {
    PointoramaWallSketch.setShowAllTriangles(show);
    const skeleton = this.getCurrentFloorWallSketch();
    skeleton?.updateSkinningVisualization();
  }

  /**
   * Handle skeleton added event.
   *
   * The tool no longer auto-generates skeleton data here — creation +
   * population is owned by the Layout bootstrap dialog and the floor
   * right-click "generate wall sketch" action, which populate the annotation
   * via `edit_skeleton`. The scene skeleton renders itself from its persisted
   * properties, so nothing is needed here beyond refreshing debug state.
   */
  private handleWallSketchAdded = (event: { type: string; skeleton?: PointoramaWallSketch }): void => {
    const skeleton = event.skeleton;
    if (!skeleton) return;
    if (skeleton.groupIdentifier === this.currentFloorGroupIdentifier) {
      this.pushFloorGroupsToCurrentWallSketch();
      this.updateSDFDebugWidget();
    }
  };

  /**
   * Generate the base skeleton from rooms (without cleanup passes)
   * Returns the skeleton and debug data, or null if no rooms available
   */
  private generateBaseSkeleton(): { skeleton: FloorSkeleton; debugData: SkeletonDebugData } | null {
    const rooms = this.getRoomsOnCurrentFloor();
    if (rooms.length === 0) {
      return null;
    }

    const floorHeight = this.currentFloorFloorPlane?.height ?? 0;

    // Convert rooms to the format expected by generateSkeletonFromRooms
    const roomData = rooms.map((room) => ({
      identifier: room.identifier,
      polygon: room.getPolygon(),
    }));

    // Pull configurable Create Nodes kwargs from the pipeline so they reach
    // the probe sweep (which runs here, before the pipeline executes).
    const createNodesPass = this.pipeline.getPassByName('Create Nodes');
    const probeMaxWalk = createNodesPass?.kwargs?.probeMaxWalk;

    // Reuse the floor's outline (merged sketch rings first, then the legacy
    // annotation the skeleton was built from) as the wall-region envelope so
    // regenerate stays consistent with the initial bootstrap.
    const linkedRings = this.getLinkedOutlineRings();
    const precomputedWallRegion = linkedRings ? ringsToWallRegion(linkedRings) : undefined;

    // Generate initial skeleton (without cleanup passes - those are run through pipeline)
    return generateSkeletonFromRooms(
      roomData,
      floorHeight,
      {
        ...this.expansionConfig,
        ...(probeMaxWalk !== undefined ? { probeMaxWalk } : {}),
        skipCleanupPasses: true,
      },
      precomputedWallRegion,
    );
  }

  /**
   * The floor's outline rings, or null when it has none: the sketch's own
   * merged `role:'OUTLINE'` rings win; a legacy OUTLINE annotation linked in
   * the skeleton's metadata (persisted via `skeleton_metadata`) is the
   * fallback for unconverted projects.
   */
  private getLinkedOutlineRings(): OutlineRingComponent[] | null {
    const sceneWallSketch = this.getCurrentFloorWallSketch();
    if (!sceneWallSketch) return null;
    const merged = extractOutlineRings(sceneWallSketch.getNodes(), sceneWallSketch.getAllEdges());
    if (merged.length > 0) return merged;
    const outlineId = sceneWallSketch.getOutlineIdentifier();
    if (!outlineId) return null;
    const outlines = this.viewer.scene.outlines as PointoramaOutline[];
    const outline = outlines.find((o) => o.identifier === outlineId) ?? null;
    if (!outline) return null;
    const outerRing = outline.getOuterRing().map((p) => ({ x: p.x, y: p.y, z: p.z }));
    if (outerRing.length < 3) return null;
    return [{ outerRing, holes: outline.getHoles().map((h) => h.map((p) => ({ x: p.x, y: p.y, z: p.z }))) }];
  }

  /**
   * Generate skeleton from rooms on the current floor
   */
  private async generateWallSketch(): Promise<void> {
    const result = this.generateBaseSkeleton();
    if (!result) {
      this.generatedWallSketch = null;
      this.debugData = null;
      return;
    }

    // Capture undo state before modifying (only if skeleton already exists)
    this.pushWallSketchUndoSnapshot();

    this.debugData = result.debugData;

    // Run all enabled passes through the pipeline
    try {
      this.generatedWallSketch = await this.pipeline.process(result.skeleton);
      this.debugWidget?.updateTimings();
    } catch (error) {
      console.error('Pipeline processing failed:', error);
      this.generatedWallSketch = result.skeleton;
    }

    // Update the scene skeleton with the generated data
    this.updateSceneWallSketch();
    this.updateDebugVisualization();

    // Update SDF debug widget (skeleton data changed, SDF may regenerate)
    // Use a small delay to allow the skeleton's SDF to regenerate
    setTimeout(() => this.updateSDFDebugWidget(), 100);
  }

  /**
   * Update the scene skeleton with the generated data and save to database.
   * This is only called when pipeline regenerates (via debug widget).
   */
  private updateSceneWallSketch(): void {
    if (!this.generatedWallSketch || !this.currentFloorGroupIdentifier) return;

    const sceneWallSketch = this.getCurrentFloorWallSketch();
    if (sceneWallSketch) {
      // Update the existing scene skeleton visualization
      // The generator emits its own ids — keep them so the references it
      // built (startNodeId / endNodeId / edgeIds / roomIds) keep resolving.
      // Treat the generator's id as the human name; future edits via
      // WallSketchEditSession will mint proper UUIDs for new entries.
      const nodes = Array.from(this.generatedWallSketch.nodes.values()).map((node) => ({
        id: node.id,
        name: node.id,
        position: { x: node.position.x, y: node.position.y, z: node.position.z },
        roomIds: node.roomIds,
        edgeIds: node.edgeIds,
        metadata: node.metadata ?? '',
      }));
      const edges = Array.from(this.generatedWallSketch.edges.values()).map((edge) => ({
        id: edge.id,
        name: edge.id,
        startNodeId: edge.startNodeId,
        endNodeId: edge.endNodeId,
        roomIds: edge.roomIds,
        leftSize: edge.leftSize ?? 0.1,
        rightSize: edge.rightSize ?? 0.1,
        wallType: edge.wallType ?? 'INNER',
        metadata: edge.metadata ?? '',
      }));

      // Regeneration rebuilds only the WALL edges — the merged outline
      // subgraph (`role:'OUTLINE'`) carries over verbatim.
      appendExistingOutlineSubgraph(sceneWallSketch, nodes, edges);

      // Persist the skin's pass-through thickness weld (connected near-equal
      // walls render as one width — make it authoritative on the edge data).
      const weldedEdges = applyWeldedEdgeSizes(nodes, edges);

      // Preserve the existing skeleton-level metadata (including the
      // linked outline id) across regenerate — without this the
      // linkage gets wiped on `setData(..., null)` and the wall-
      // region recompute below falls through to synthesizing a fresh
      // envelope from rooms instead of reusing the linked outline.
      const preservedMetadata = sceneWallSketch.getMetadata();
      sceneWallSketch.setData(nodes, weldedEdges, preservedMetadata);

      // Wall-region overlay tracks the rooms used to generate this skeleton.
      // Computing it here means it's available + persisted as soon as the
      // skeleton itself is, mirroring the "shows up automatically with the
      // skeleton" expectation. This is also our auto-bootstrap path for
      // legacy wallSketches (their wallRegions property starts empty until the
      // user regenerates).
      this.recomputeWallRegionsForCurrentFloor(sceneWallSketch);

      // Dispatch event to save to database
      this.viewer.scene.dispatchEvent({
        type: 'edit_skeleton',
        value: {
          annotationIdentifier: sceneWallSketch.identifier,
          nodes,
          edges: weldedEdges,
          metadata: preservedMetadata ?? undefined,
        },
      });
    }
  }

  /**
   * Recompute the wall-region overlay for `sceneWallSketch`. When the
   * skeleton is linked to an outline (via its metadata) the overlay
   * is just that outline's rings — re-synthesizing one from the rooms
   * would draw a second, slightly-different envelope on top of the
   * user-curated outline. Otherwise we fall back to synthesizing one
   * from the rooms with the configured outer wall thickness.
   */
  private recomputeWallRegionsForCurrentFloor(sceneWallSketch: PointoramaWallSketch): void {
    const linkedRings = this.getLinkedOutlineRings();
    if (linkedRings) {
      sceneWallSketch.setWallRegions(
        linkedRings.map((c) => ({
          outerRing: c.outerRing.map((p) => ({ x: p.x, y: p.y })),
          holes: c.holes.map((h) => h.map((p) => ({ x: p.x, y: p.y }))),
        })),
      );
      return;
    }

    const rooms = this.getRoomsOnCurrentFloor();
    if (rooms.length === 0) return;
    const roomData = rooms.map((room) => ({
      identifier: room.identifier,
      polygon: room.getPolygon(),
    }));
    const thickness = this.expansionConfig.outerWallThickness ?? 0.3;
    sceneWallSketch.recomputeWallRegions(roomData, thickness);
  }

  /**
   * Run pipeline passes - always starts from fresh base skeleton
   * If passName is '__all__', runs all enabled passes
   * If passName is a specific pass, runs only up to and including that pass
   */
  private async runPass(passName: string): Promise<void> {
    // Always regenerate base skeleton first
    const baseResult = this.generateBaseSkeleton();
    if (!baseResult) return;

    // Capture undo state before modifying
    this.pushWallSketchUndoSnapshot();

    this.debugData = baseResult.debugData;
    let skeleton = baseResult.skeleton;

    this.pipeline.passTimings.clear();

    if (passName === '__all__') {
      // Run all enabled passes through the pipeline
      try {
        skeleton = await this.pipeline.process(skeleton);
      } catch (error) {
        console.error('Pipeline processing failed:', error);
      }
    } else {
      // Run only the specified pass (regardless of enabled state)
      const pass = this.pipeline.getPassByName(passName);
      if (!pass) {
        console.warn(`Pass not found: ${passName}`);
        return;
      }

      const startTime = performance.now();
      try {
        const result = pass.processor(skeleton, pass.kwargs);
        skeleton = result instanceof Promise ? await result : result;
        const elapsed = performance.now() - startTime;
        this.pipeline.passTimings.set(passName, elapsed);
      } catch (error) {
        console.error(`Pass ${passName} failed:`, error);
      }
    }

    this.generatedWallSketch = skeleton;
    this.updateSceneWallSketch();
    this.updateDebugVisualization();
    this.debugWidget?.updateTimings();
  }

  /**
   * Get the processing pipeline
   */
  getPipeline(): SkeletonProcessingPipeline {
    return this.pipeline;
  }

  /**
   * Get rooms on the current floor
   */
  private getRoomsOnCurrentFloor(): PointoramaRoom[] {
    if (this.currentFloorRoomIdentifiers.length > 0) {
      return this.viewer.scene.rooms.filter((room) => this.currentFloorRoomIdentifiers.includes(room.identifier));
    }

    // Fallback: explicit level membership (half-open up to the next floor) so
    // rooms near the slab aren't dropped under true-ceiling semantics, nor
    // double-claimed by stacked levels.
    const floorGroupId = this.currentFloorGroupIdentifier;
    const bounds = floorGroupId ? this.viewer.floorIsolationManager.resolveLevelBounds(floorGroupId) : null;
    if (bounds) {
      return this.viewer.scene.rooms.filter((room) => {
        const polygon = room.getPolygon();
        return polygon.length > 0 && isZWithinLevel(bounds, polygon[0].z);
      });
    }

    if (this.currentFloorFloorPlane) {
      const floorHeight = this.currentFloorFloorPlane.height;
      const ceilingHeight = this.currentFloorCeilingPlane?.height ?? floorHeight + FALLBACK_INTERIOR_HEIGHT_M;
      const tolerance = 0.5;

      return this.viewer.scene.rooms.filter((room) => {
        const polygon = room.getPolygon();
        if (polygon.length === 0) return false;
        const roomZ = polygon[0].z;
        return roomZ >= floorHeight - tolerance && roomZ <= ceilingHeight + tolerance;
      });
    }

    return [];
  }

  /**
   * Build the rasterized-walls overlay: a textured plane showing the raster
   * grid the raster-thin skeleton pass worked on (filled walls in grey, the
   * thinned 1-px skeleton in green). Lives in its own debug group so the widget
   * checkbox can toggle it. No-op when no raster pass has run.
   */
  private updateRasterDebugVisualization(): void {
    const rd = this.generatedWallSketch?.rasterDebug;
    if (!rd) return;

    const floorHeight = this.generatedWallSketch?.floorHeight ?? 0;
    // RGBA texture: wall = grey, thinned skeleton = green, void = transparent.
    const { w, h, wall, thinned } = rd;
    const tex = new Uint8Array(w * h * 4);
    for (let i = 0; i < w * h; i++) {
      const o = i * 4;
      if (thinned[i]) {
        tex[o] = 0;
        tex[o + 1] = 255;
        tex[o + 2] = 90;
        tex[o + 3] = 255;
      } else if (wall[i]) {
        tex[o] = 120;
        tex[o + 1] = 120;
        tex[o + 2] = 120;
        tex[o + 3] = 150;
      } else {
        tex[o + 3] = 0;
      }
    }
    const dataTex = new THREE.DataTexture(tex, w, h, THREE.RGBAFormat);
    dataTex.magFilter = THREE.NearestFilter;
    dataTex.minFilter = THREE.NearestFilter;
    dataTex.needsUpdate = true;

    // Plane spans the grid extent in world coords. Grid row 0 is at the bottom
    // (oy), and DataTexture row 0 is its first row, so no Y flip is needed.
    const worldW = w * rd.cell;
    const worldH = h * rd.cell;
    const geom = new THREE.PlaneGeometry(worldW, worldH);
    const mat = new THREE.MeshBasicMaterial({ map: dataTex, transparent: true, depthTest: false, depthWrite: false });
    const plane = new THREE.Mesh(geom, mat);
    // Slightly below the skeleton edges so lines stay readable on top.
    plane.position.set(rd.ox + worldW / 2, rd.oy + worldH / 2, floorHeight - 0.01);
    plane.renderOrder = -1;

    const show = this.debugWidget?.getDebugOptions().showRasterImage ?? false;
    const group = createDebugGroup(this.viewer, SKELETON_RASTER_DEBUG_GROUP_NAME, { visible: show });
    group.add(plane);
  }

  /**
   * Clear debug visualization groups
   */
  private clearDebugVisualization(): void {
    // Remove existing debug groups from scene
    const raycastGroup = this.viewer.scene.scene.children.find((child) => child.name === SKELETON_RAYCAST_DEBUG_GROUP_NAME);
    if (raycastGroup) {
      this.viewer.scene.scene.remove(raycastGroup);
      raycastGroup.traverse((child) => {
        const mesh = child as THREE.Mesh;
        if (mesh.geometry) mesh.geometry.dispose();
        if (mesh.material) {
          if (Array.isArray(mesh.material)) {
            mesh.material.forEach((m) => m.dispose());
          } else {
            mesh.material.dispose();
          }
        }
      });
    }

    const bisectorGroup = this.viewer.scene.scene.children.find((child) => child.name === SKELETON_BISECTOR_DEBUG_GROUP_NAME);
    if (bisectorGroup) {
      this.viewer.scene.scene.remove(bisectorGroup);
      bisectorGroup.traverse((child) => {
        const mesh = child as THREE.Mesh;
        if (mesh.geometry) mesh.geometry.dispose();
        if (mesh.material) {
          if (Array.isArray(mesh.material)) {
            mesh.material.forEach((m) => m.dispose());
          } else {
            mesh.material.dispose();
          }
        }
      });
    }

    const straightenGroup = this.viewer.scene.scene.children.find((child) => child.name === SKELETON_STRAIGHTEN_DEBUG_GROUP_NAME);
    if (straightenGroup) {
      this.viewer.scene.scene.remove(straightenGroup);
      straightenGroup.traverse((child) => {
        const mesh = child as THREE.Mesh;
        if (mesh.geometry) mesh.geometry.dispose();
        if (mesh.material) {
          if (Array.isArray(mesh.material)) {
            mesh.material.forEach((m) => m.dispose());
          } else {
            mesh.material.dispose();
          }
        }
      });
    }

    // Wall-region visualization is now persisted on PointoramaWallSketch and
    // always rendered, so the debug group is no longer needed.

    const sourceVertexGroup = this.viewer.scene.scene.children.find((child) => child.name === SKELETON_SOURCE_VERTEX_DEBUG_GROUP_NAME);
    if (sourceVertexGroup) {
      this.viewer.scene.scene.remove(sourceVertexGroup);
      sourceVertexGroup.traverse((child) => {
        const mesh = child as THREE.Mesh;
        if (mesh.geometry) mesh.geometry.dispose();
        if (mesh.material) {
          if (Array.isArray(mesh.material)) {
            mesh.material.forEach((m) => m.dispose());
          } else {
            mesh.material.dispose();
          }
        }
      });
    }

    const probeGroupNames = [SKELETON_PROBE_ORIGIN_DEBUG_GROUP_NAME, SKELETON_PROBE_RAY_DEBUG_GROUP_NAME, SKELETON_PROBE_MIDPOINT_DEBUG_GROUP_NAME, SKELETON_PROBE_CENTERLINE_DEBUG_GROUP_NAME, SKELETON_RASTER_DEBUG_GROUP_NAME];
    for (const groupName of probeGroupNames) {
      const probeGroup = this.viewer.scene.scene.children.find((child) => child.name === groupName);
      if (!probeGroup) continue;
      this.viewer.scene.scene.remove(probeGroup);
      probeGroup.traverse((child) => {
        const mesh = child as THREE.Mesh;
        if (mesh.geometry) mesh.geometry.dispose();
        if (mesh.material) {
          if (Array.isArray(mesh.material)) {
            mesh.material.forEach((m) => m.dispose());
          } else {
            mesh.material.dispose();
          }
        }
      });
    }
  }

  /**
   * Update debug visualization with raycast data
   */
  private updateDebugVisualization(): void {
    this.clearDebugVisualization();

    // Rasterized-walls overlay (raster-thin pass) is independent of the
    // per-vertex debugData, so build it before the early-return below.
    this.updateRasterDebugVisualization();

    if (!this.debugData) return;

    const floorHeight = this.generatedWallSketch?.floorHeight ?? 0;

    // Anchor all debug groups at a near-the-data origin so float32 line
    // geometry stays precise even when the scene sits far from world (0,0,0).
    let debugOrigin: THREE.Vector3 | undefined;
    for (const poly of this.debugData.roomPolygonsById.values()) {
      if (poly.length > 0) {
        debugOrigin = new THREE.Vector3(poly[0].x, poly[0].y, floorHeight);
        break;
      }
    }

    // Create raycast debug group
    const raycastGroup = createDebugGroup(this.viewer, SKELETON_RAYCAST_DEBUG_GROUP_NAME, { origin: debugOrigin });

    // Create bisector debug group
    const bisectorGroup = createDebugGroup(this.viewer, SKELETON_BISECTOR_DEBUG_GROUP_NAME, { origin: debugOrigin });

    // Add debug rays for each vertex
    for (const vertexData of this.debugData.vertices) {
      // Add raycast visualization (only for convex vertices that cast rays)
      if (vertexData.raycasts) {
        const raycasts = vertexData.raycasts;
        // Add all rays from this vertex
        raycasts.rays.forEach((ray, index) => {
          const rayOrigin = new THREE.Vector3(ray.origin.x, ray.origin.y, floorHeight + 0.03);
          const rayDirection = new THREE.Vector3(ray.direction.x, ray.direction.y, 0).normalize();

          // Highlight the ray that was used (shortest hit)
          const wasUsed = index === raycasts.usedRayIndex;
          addRayToDebugGroup(raycastGroup, rayOrigin, rayDirection, ray.distance, ray.hasHit, {
            hitColor: wasUsed ? 0x00ff00 : 0x008800, // Bright green if used, dim green if not
            missColor: 0xff6600,
            opacity: wasUsed ? 0.8 : 0.4,
          });
        });
      }

      // Add bisector direction visualization
      const bisectorOrigin = new THREE.Vector3(vertexData.originalPosition.x, vertexData.originalPosition.y, floorHeight + 0.04);
      const bisectorDir = new THREE.Vector3(vertexData.bisectorDirection.x, vertexData.bisectorDirection.y, 0).normalize();

      // Show bisector as short line (0.2m)
      addRayToDebugGroup(
        bisectorGroup,
        bisectorOrigin,
        bisectorDir,
        0.2,
        true, // Always show as "hit" color
        {
          hitColor: vertexData.isConvex ? 0x00ffff : 0xff00ff, // Cyan for convex, magenta for concave
          opacity: 0.8,
        },
      );
    }

    // Wall-region overlay is rendered by PointoramaWallSketch itself now (in
    // the annotation color, persisted via the property system). The
    // wallRegion field on debugData is still produced by the pipeline but no
    // longer drawn here — the recompute trigger writes through the skeleton.

    // Source-vertex debug: line from each skeleton node to each of its
    // original (roomId, vertexIndex) source positions — useful for verifying
    // node provenance after merges.
    const sourceVertexGroup = createDebugGroup(this.viewer, SKELETON_SOURCE_VERTEX_DEBUG_GROUP_NAME, { origin: debugOrigin });
    if (this.generatedWallSketch) {
      const polysById = this.debugData.roomPolygonsById;
      for (const node of this.generatedWallSketch.nodes.values()) {
        for (const sv of node.sourceVertices) {
          const poly = polysById.get(sv.roomId);
          if (!poly || sv.vertexIndex >= poly.length) continue;
          const src = poly[sv.vertexIndex];
          const dx = src.x - node.position.x;
          const dy = src.y - node.position.y;
          const len = Math.hypot(dx, dy);
          if (len < 1e-6) continue;
          const origin = new THREE.Vector3(node.position.x, node.position.y, floorHeight + 0.06);
          const direction = new THREE.Vector3(dx / len, dy / len, 0);
          addRayToDebugGroup(sourceVertexGroup, origin, direction, len, true, {
            hitColor: 0x66aaff,
            opacity: 0.6,
          });
        }
      }
    }

    // Probe debug: per-vertex probes used by the probe-based expansion.
    // - Origins (small dots) sit 5cm in along each adjacent edge.
    // - Rays go perpendicular to the edge, into the wall material.
    // - Midpoints sit on the wall centerline (half the hit distance).
    // - Centerlines run parallel to the source edge through the midpoint.
    const probeOriginGroup = createDebugGroup(this.viewer, SKELETON_PROBE_ORIGIN_DEBUG_GROUP_NAME, { origin: debugOrigin });
    const probeRayGroup = createDebugGroup(this.viewer, SKELETON_PROBE_RAY_DEBUG_GROUP_NAME, { origin: debugOrigin });
    const probeMidpointGroup = createDebugGroup(this.viewer, SKELETON_PROBE_MIDPOINT_DEBUG_GROUP_NAME, { origin: debugOrigin });
    const probeCenterlineGroup = createDebugGroup(this.viewer, SKELETON_PROBE_CENTERLINE_DEBUG_GROUP_NAME, { origin: debugOrigin });
    const centerlineHalfLength = 1.0;
    for (const vertexData of this.debugData.vertices) {
      if (!vertexData.probes) continue;
      for (const probe of vertexData.probes) {
        const originAt = new THREE.Vector3(probe.origin.x, probe.origin.y, floorHeight + 0.07);
        // Extension-pass probes are the fallback sweep that runs only
        // when the primary `[vertex → midpoint]` pass produced zero
        // hits — pink/cyan for primary, orange tones for extension.
        const originColor = probe.isExtension ? 0xff9900 : 0xff66cc;
        // Hit color: bright lime green for primary probes, deep
        // teal for extension probes. The lime is well-separated
        // from the cyan/blue used elsewhere in the debug viz (snap
        // overlays, wall-region outlines) so a successful probe
        // ray stands out unambiguously at any zoom level.
        const hitColor = probe.isExtension ? 0x008888 : 0x66ff00;
        // Color-code misses by REASON so the user can see which
        // filter ate a visually-obvious hit:
        //  - too-far        → gray   (range cap; longer ray would hit)
        //  - wrong-side     → red    (ray came from inside the polygon)
        //  - too-oblique    → yellow (>45° off perpendicular)
        //  - only-skipped   → purple (closest crossing was on a skipped
        //                              adjacent edge — typically a
        //                              tight corner where the outward
        //                              direction exits through one of
        //                              the vertex's own walls)
        //  - no-intersection → near-black (the ray's path truly
        //                                   crossed no wall segment)
        // Extension probes use dimmed variants so primary vs. extension
        // is still distinguishable at a glance.
        // Maximally-distinct miss palette — five reasons mapped to
        // five colors evenly spaced around the hue wheel so no two
        // are confusable at a glance. Extension variants share the
        // same base hue but desaturated (mixed with gray) so primary
        // vs. extension is still distinguishable.
        //   too-far         → pure RED       (#ff0000)
        //   wrong-side      → pure GREEN     (#00ff00)
        //   too-oblique     → pure BLUE      (#0000ff)
        //   only-skipped    → pure ORANGE    (#ff8800)
        //   no-intersection → pure WHITE     (#ffffff)
        let missColor: number;
        switch (probe.missReason) {
          case 'too-far':
            missColor = probe.isExtension ? 0x884444 : 0xff0000;
            break;
          case 'wrong-side':
            missColor = probe.isExtension ? 0x448844 : 0x00ff00;
            break;
          case 'too-oblique':
            missColor = probe.isExtension ? 0x444488 : 0x0000ff;
            break;
          case 'only-skipped':
            missColor = probe.isExtension ? 0x886644 : 0xff8800;
            break;
          case 'no-intersection':
          default:
            missColor = probe.isExtension ? 0x888888 : 0xffffff;
            break;
        }
        addPointToDebugGroup(probeOriginGroup, originAt, { color: originColor, size: 0.002, opacity: 0.9 });

        const rayOrigin = new THREE.Vector3(probe.origin.x, probe.origin.y, floorHeight + 0.07);
        addRayToDebugGroup(probeRayGroup, rayOrigin, probe.rayDirection.clone(), probe.rayDistance, probe.hasHit, {
          hitColor,
          missColor,
          opacity: 0.8,
        });

        const midAt = new THREE.Vector3(probe.centerlineMidpoint.x, probe.centerlineMidpoint.y, floorHeight + 0.07);
        addPointToDebugGroup(probeMidpointGroup, midAt, { color: 0xffff66, size: 0.04, opacity: 0.9 });

        // Centerline is only drawn for the chosen (canonical) probe — the one whose
        // line is actually used in the intersection that defines the expanded vertex.
        if (probe.isChosen) {
          const edgeDir = probe.edgeDirection;
          const lineStart = new THREE.Vector3(probe.centerlineMidpoint.x - edgeDir.x * centerlineHalfLength, probe.centerlineMidpoint.y - edgeDir.y * centerlineHalfLength, floorHeight + 0.07);
          addRayToDebugGroup(probeCenterlineGroup, lineStart, edgeDir.clone(), centerlineHalfLength * 2, true, {
            hitColor: 0xffaa00,
            opacity: 0.6,
          });
        }
      }
    }

    // Create straighten debug group and add rays from skeleton
    const straightenGroup = createDebugGroup(this.viewer, SKELETON_STRAIGHTEN_DEBUG_GROUP_NAME, { origin: debugOrigin });

    // One debug group per pair-walls escalation stage. Routing is by
    // label prefix so each stage's rays go into its own toggle.
    const pairWallsMatchNearGroup = createDebugGroup(this.viewer, SKELETON_PAIR_WALLS_MATCH_NEAR_DEBUG_GROUP_NAME, { origin: debugOrigin });
    const pairWallsRayNearGroup = createDebugGroup(this.viewer, SKELETON_PAIR_WALLS_RAY_NEAR_DEBUG_GROUP_NAME, { origin: debugOrigin });
    const pairWallsMatchInfGroup = createDebugGroup(this.viewer, SKELETON_PAIR_WALLS_MATCH_INF_DEBUG_GROUP_NAME, { origin: debugOrigin });
    const pairWallsRayInfGroup = createDebugGroup(this.viewer, SKELETON_PAIR_WALLS_RAY_INF_DEBUG_GROUP_NAME, { origin: debugOrigin });
    const pruneMatchGroup = createDebugGroup(this.viewer, SKELETON_PRUNE_MATCH_DEBUG_GROUP_NAME, { origin: debugOrigin });
    const cornerMatchGroup = createDebugGroup(this.viewer, SKELETON_CORNER_MATCH_DEBUG_GROUP_NAME, { origin: debugOrigin });

    if (this.generatedWallSketch?.debugRays) {
      for (const ray of this.generatedWallSketch.debugRays) {
        const rayOrigin = new THREE.Vector3(ray.origin.x, ray.origin.y, floorHeight + 0.02);

        // Pair-walls rays each go to their stage's dedicated group so
        // toggles can isolate them. Color is consistent within the
        // stage (green hit / red miss) since the stage choice is the
        // information the user actually needs.
        switch (ray.label) {
          case 'pair-walls-match-near':
            addRayToDebugGroup(pairWallsMatchNearGroup, rayOrigin, ray.direction, ray.distance, ray.hit, { hitColor: 0x00ff00, missColor: 0xff0000, opacity: 0.6 });
            continue;
          case 'pair-walls-ray-near':
            addRayToDebugGroup(pairWallsRayNearGroup, rayOrigin, ray.direction, ray.distance, ray.hit, { hitColor: 0x00ff00, missColor: 0xff0000, opacity: 0.6 });
            continue;
          case 'pair-walls-match-inf':
            addRayToDebugGroup(pairWallsMatchInfGroup, rayOrigin, ray.direction, ray.distance, ray.hit, { hitColor: 0x00ff00, missColor: 0xff0000, opacity: 0.6 });
            continue;
          case 'pair-walls-ray-inf':
            addRayToDebugGroup(pairWallsRayInfGroup, rayOrigin, ray.direction, ray.distance, ray.hit, { hitColor: 0x00ff00, missColor: 0xff0000, opacity: 0.6 });
            continue;
          // Prune-pass matching ties: green = polygon edge locked this
          // trace, red = candidate rejected (span already claimed).
          case 'prune-match':
            addRayToDebugGroup(pruneMatchGroup, rayOrigin, ray.direction, ray.distance, ray.hit, { hitColor: 0x00ff00, missColor: 0xff0000, opacity: 0.8 });
            continue;
          // Thickness corner-match ties: node → claimed polygon corner.
          case 'corner-match':
            addRayToDebugGroup(cornerMatchGroup, rayOrigin, ray.direction, ray.distance, ray.hit, { hitColor: 0xffcc00, missColor: 0xff0000, opacity: 0.9 });
            continue;
        }

        // Default route: straighten group. Inner-wall hits get cyan
        // so they stand out from regular green hits.
        const isInnerWall = ray.label?.includes('inner');
        addRayToDebugGroup(straightenGroup, rayOrigin, ray.direction, ray.distance, ray.hit, {
          hitColor: isInnerWall ? 0x00ffff : 0x00ff00,
          missColor: 0xff0000,
          opacity: 0.5,
        });
      }
    }
  }

  /**
   * Get the current skeleton data
   */
  getWallSketch(): FloorSkeleton | null {
    return this.generatedWallSketch;
  }

  // ─── Undo/Redo ──────────────────────────────────────────────────────

  /**
   * Capture an undo snapshot of the current scene skeleton state.
   * Delegates to the edit session so all snapshot logic lives in one place.
   * Used by pipeline regeneration paths (generateWallSketch, runPass) that
   * mutate skeleton data outside of an interactive edit session.
   */
  private pushWallSketchUndoSnapshot(): void {
    this.editSession.pushWallSketchUndoSnapshot();
  }

  /**
   * Update the SDF debug widget with current skeleton's edge texture data
   */
  private updateSDFDebugWidget(): void {
    if (!this.sdfDebugWidget) return;

    const sceneWallSketch = this.getCurrentFloorWallSketch();
    if (sceneWallSketch && sceneWallSketch.isSDFEnabled()) {
      const edgeTexture = sceneWallSketch.getEdgeTexture();
      const edgeCount = sceneWallSketch.getEdgeCount();
      if (edgeTexture && edgeCount > 0) {
        // Get SDF settings from the skeleton (access private field)
        const skeleton = sceneWallSketch as unknown as { _sdfMaxDistance?: number };
        // The SDF vertical band derives from the floor group's range (ceiling −
        // floor), matching PointoramaWallSketch.getSdfHeightTolerance.
        const floorHeight = sceneWallSketch.getFloorHeight();
        const ceiling = sceneWallSketch.getCeilingHeight();
        const heightTolerance = ceiling !== null && ceiling - floorHeight > 0.1 ? ceiling - floorHeight : 2.5;
        this.sdfDebugWidget.setEdgeTextureInfo({
          texture: edgeTexture,
          edgeCount: edgeCount,
          maxDistance: skeleton._sdfMaxDistance || 5.0,
          floorHeight,
          heightTolerance,
        });
        return;
      }
    }
    this.sdfDebugWidget.setSDFResult(null);
  }

  // ─── Interactive editing input ──────────────────────────────────────

  public onClick(event: MouseEvent): void {
    if (event.button === 1) return; // middle-click is reserved for camera pan
    this.editSession.onClick(event);
  }

  public onRightClick(event: MouseEvent): void {
    this.editSession.onRightClick(event);
  }

  /**
   * Capture hover at the click pixel before any drag threshold is crossed.
   * Without this, a fast click-drag would only refresh hover at the position
   * 5px into the drag, by which point the cursor may have left the node sphere
   * and the raycast misses — making the drag silently do nothing.
   */
  public onMouseDown(event: MouseEvent): void {
    if (event.button === 1) return; // middle-click is reserved for camera pan
    // Floor without a wall sketch yet: kick the (async, React-side) creation
    // of an EMPTY sketch now, so the wall-draw drag that may follow can start
    // as soon as the scene object lands — see onDragStart. Creation-capable
    // hosts only: the edit tool must not conjure sketches out of thin air.
    if (event.button === 0 && this.capabilities.allowCreate && !this.getCurrentFloorWallSketch() && this.currentFloorGroupIdentifier) {
      this.requestEmptySketch();
    }
    this.editSession.onMouseDown(event);
  }

  /** Drag-start buffered while the empty sketch requested in onMouseDown is
   *  still being created; replayed as a draw-wall drag when it arrives. */
  private pendingDrawDragStart: DragEventData | null = null;
  private sketchCreationListener: ((e: { skeleton?: PointoramaWallSketch }) => void) | null = null;

  /** Ask the React layer to create an EMPTY wall sketch for the current floor
   *  (`request_skeleton` with no rooms — the same channel the layout bootstrap
   *  uses). When it arrives, any buffered drag becomes the first wall. */
  private requestEmptySketch(): void {
    const groupId = this.currentFloorGroupIdentifier;
    if (!groupId || this.sketchCreationListener) return;
    const onAdded = (e: { skeleton?: PointoramaWallSketch }) => {
      if (!e.skeleton || e.skeleton.groupIdentifier !== groupId) return;
      this.clearSketchCreationListener();
      const pending = this.pendingDrawDragStart;
      this.pendingDrawDragStart = null;
      if (pending) this.editSession.startDrawWall(pending);
    };
    this.sketchCreationListener = onAdded;
    this.viewer.scene.addEventListener('wall_sketch_added', onAdded);
    this.viewer.scene.dispatchEvent({
      type: 'request_skeleton',
      value: {
        floorGroupIdentifier: groupId,
        floorHeight: this.currentFloorFloorPlane?.height ?? 0,
        roomIdentifiers: [],
      },
    });
  }

  private clearSketchCreationListener(): void {
    if (this.sketchCreationListener) {
      this.viewer.scene.removeEventListener('wall_sketch_added', this.sketchCreationListener);
      this.sketchCreationListener = null;
    }
  }

  public onDragStart(data: DragEventData): void {
    if (data.button === 1) return; // middle-button drag is camera pan, never an edit
    // Sketch creation (kicked in onMouseDown) still in flight — buffer this
    // drag start; it becomes the first wall the moment the sketch lands.
    if (data.button === 0 && this.capabilities.allowCreate && !this.getCurrentFloorWallSketch() && this.currentFloorGroupIdentifier) {
      this.pendingDrawDragStart = data;
      return;
    }
    this.editSession.onDragStart(data);
  }

  public onDrag(data: DragEventData): void {
    if (data.button === 1) return;
    this.editSession.onDrag(data);
  }

  public onDragEnd(data: DragEventData): void {
    if (data.button === 1) return;
    // Released before the sketch creation landed — nothing to draw.
    if (this.pendingDrawDragStart) {
      this.pendingDrawDragStart = null;
      return;
    }
    this.editSession.onDragEnd(data);
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (!this.editSession.isEditing) return;

    // When a text/number input is focused (e.g. the context menu's "set
    // thickness" field), let it own the keyboard. Otherwise Delete/Backspace
    // typed into the field would also delete the selected skeleton geometry.
    const target = event.target as HTMLElement | null;
    const active = (target ?? document.activeElement) as HTMLElement | null;
    if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable)) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.editSession.cancel();
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.editSession.commit();
      return;
    }

    if (event.key === 'Delete' || event.key === 'Backspace') {
      if (this.editSession.deleteSelection()) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      }
    }
  }

  /**
   * Create the hover tooltip element
   */
  private createHoverTooltip(): void {
    this.hoverTooltip = document.createElement('div');
    this.hoverTooltip.style.cssText = `
      position: fixed;
      background: rgba(0, 0, 0, 0.85);
      color: #fff;
      padding: 8px 12px;
      border-radius: 4px;
      font-family: monospace;
      font-size: 12px;
      pointer-events: none;
      z-index: 10000;
      display: none;
      max-width: 300px;
      white-space: pre-wrap;
      border: 1px solid rgba(0, 255, 136, 0.5);
    `;
    document.body.appendChild(this.hoverTooltip);
  }

  /**
   * Destroy the hover tooltip element. The hovered-marker highlight is
   * owned by `skeleton.getHover()`; clearing it there is the session's
   * job on tool disable (`updateHover` returning empty), so this only
   * tears down the DOM tooltip.
   */
  private destroyHoverTooltip(): void {
    if (this.hoverTooltip) {
      document.body.removeChild(this.hoverTooltip);
      this.hoverTooltip = null;
    }
  }

  /**
   * Run the session's hover update, then render details for whatever
   * the skeleton's shared hover state ended up holding. Reading from
   * `skeleton.getHover()` (rather than the session's return value)
   * keeps this tool consistent with any future tool that also writes
   * into the hover state, and matches the same one-source-of-truth
   * pattern as `skeleton.getSelection()`.
   */
  private handleMouseMoveForHover = (event: MouseEvent): void => {
    this.editSession.updateHover(event);

    // Pointer cursor when hovering anything clickable — a door/window glyph
    // (click selects the opening) or a wall node/edge (click selects/splits);
    // crosshair otherwise (the tool's default editing cursor).
    this.viewer.renderer.domElement.style.cursor = this.editSession.getHoveredAnnotationId() || this.editSession.hasHoverTarget() ? 'pointer' : 'crosshair';

    const sceneWallSketch = this.getCurrentFloorWallSketch();
    if (!sceneWallSketch || !this.hoverTooltip) return;
    const hover = sceneWallSketch.getHover();

    // The hover info panel is developer debug UI — gated behind the
    // super-admin "show debug ui" user setting (off by default).
    const html = this.isDebugUiEnabled() ? this.buildHoverTooltipHTML(sceneWallSketch, hover.getHoveredNodeId(), hover.getHoveredEdgeId()) : null;
    if (html) {
      this.hoverTooltip.innerHTML = html;
      this.hoverTooltip.style.display = 'block';
      this.hoverTooltip.style.left = `${event.clientX + 15}px`;
      this.hoverTooltip.style.top = `${event.clientY + 15}px`;
    } else {
      this.hoverTooltip.style.display = 'none';
    }
  };

  /** The super-admin "show debug ui" user setting (false until the property service is up). */
  private isDebugUiEnabled(): boolean {
    if (!hasGlobalPropertyService()) return false;
    try {
      return getShowDebugUiProperty().value === true;
    } catch {
      return false;
    }
  }

  /**
   * Build the tooltip HTML for the current hover target. Node and edge
   * details share the same field set (id, name, rooms, metadata) plus
   * type-specific extras — node carries probed-hit + source-vertex
   * provenance from the generation pipeline; edge carries left/right
   * wall thickness and wall type. Returns null when nothing is hovered.
   */
  private buildHoverTooltipHTML(skeleton: PointoramaWallSketch, nodeId: string | null, edgeId: string | null): string | null {
    if (nodeId) {
      const node = skeleton.getNode(nodeId);
      if (!node) return null;
      const pos = node.position;
      // Provenance fields live on the in-memory `FloorSkeleton` produced
      // by the generation pipeline, not on the persisted scene skeleton —
      // they're absent after a reload until the user regenerates.
      const richNode = this.generatedWallSketch?.nodes.get(nodeId);
      const sourceLine = richNode && richNode.sourceVertices.length > 0 ? richNode.sourceVertices.map((sv) => `${sv.roomId}#${sv.vertexIndex}`).join(', ') : '—';
      const probedLine = richNode && richNode.probedHits.length > 0 ? richNode.probedHits.map((h) => `${h.sourceRoomId}#${h.sourceVertexIndex}.${h.sourceEdgeKind} → ${h.hitRoomId}#${h.hitEdgeIndex}`).join('<br>') : '—';
      return `
<b>Node:</b> ${nodeId}
<b>Position:</b> (${pos.x.toFixed(3)}, ${pos.y.toFixed(3)}, ${pos.z.toFixed(3)})
<b>Rooms:</b> ${node.roomIds.join(', ') || '—'}
<b>Edges:</b> ${node.edgeIds.length}
<b>Source vertices:</b> ${sourceLine}
<b>Probed hits:</b><br>${probedLine}
      `.trim();
    }
    if (edgeId) {
      const edge = skeleton.getEdge(edgeId);
      if (!edge) return null;
      // Endpoint positions help confirm the right edge is highlighted at
      // a glance, especially when two parallel edges sit close together.
      const start = skeleton.getNode(edge.startNodeId);
      const end = skeleton.getNode(edge.endNodeId);
      const len = start && end ? Math.hypot(end.position.x - start.position.x, end.position.y - start.position.y).toFixed(3) : '—';
      return `
<b>Edge:</b> ${edgeId}
<b>Name:</b> ${edge.name || '—'}
<b>Endpoints:</b> ${edge.startNodeId} → ${edge.endNodeId}
<b>Length:</b> ${len} m
<b>Wall type:</b> ${edge.wallType}
<b>Thickness:</b> L ${edge.leftSize.toFixed(3)} m / R ${edge.rightSize.toFixed(3)} m
<b>Rooms:</b> ${edge.roomIds.join(', ') || '—'}
      `.trim();
    }
    return null;
  }

  dispose(): void {
    this.clearDebugVisualization();

    if (this.debugWidget) {
      this.debugWidget.destroy();
      this.debugWidget = null;
    }

    if (this.sdfDebugWidget) {
      this.sdfDebugWidget.destroy();
      this.sdfDebugWidget = null;
    }

    super.dispose();
  }
}
